import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';

interface AvatarUploadProps {
  currentAvatarUrl?: string;
  onUploadComplete: (url: string) => void;
  userId: string;
}

export const AvatarUpload: React.FC<AvatarUploadProps> = ({
  currentAvatarUrl,
  onUploadComplete,
  userId,
}) => {
  const [isUploading, setIsUploading] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      try {
        setIsUploading(true);

        // Upload to Supabase Storage
        const fileExt = file.name.split('.').pop();
        const fileName = `${userId}-${Date.now()}.${fileExt}`;
        const { data, error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('avatars')
          .getPublicUrl(fileName);

        onUploadComplete(publicUrl);
        toast.success('Avatar uploaded successfully');
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Failed to upload avatar');
      } finally {
        setIsUploading(false);
      }
    },
    [userId, onUploadComplete]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
    },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024, // 5MB
  });

  return (
    <div className="flex flex-col items-center gap-4">
      <Avatar className="h-24 w-24">
        <AvatarImage src={currentAvatarUrl} />
        <AvatarFallback>
          {isUploading ? (
            <Loader2 className="h-8 w-8 animate-spin" />
          ) : (
            userId.slice(0, 2).toUpperCase()
          )}
        </AvatarFallback>
      </Avatar>

      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-4 text-center cursor-pointer
          transition-colors duration-200 w-full max-w-xs
          ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/20'}
        `}
      >
        <input {...getInputProps()} />
        <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
        {isDragActive ? (
          <p className="text-sm">Drop the image here</p>
        ) : (
          <div className="space-y-2">
            <p className="text-sm">Drag & drop an image here, or click to select</p>
            <p className="text-xs text-muted-foreground">
              PNG, JPG or GIF (max. 5MB)
            </p>
          </div>
        )}
      </div>

      {currentAvatarUrl && (
        <Button
          variant="outline"
          size="sm"
          onClick={async () => {
            try {
              // Remove the old avatar from storage if it exists
              const oldFileName = currentAvatarUrl.split('/').pop();
              if (oldFileName) {
                await supabase.storage
                  .from('avatars')
                  .remove([oldFileName]);
              }
              onUploadComplete('');
              toast.success('Avatar removed');
            } catch (error) {
              console.error('Error removing avatar:', error);
              toast.error('Failed to remove avatar');
            }
          }}
        >
          Remove Avatar
        </Button>
      )}
    </div>
  );
}; 